"use client";

import { useEffect, useState, type ReactNode } from "react";
import { usePathname } from "next/navigation";
import { I18nextProvider } from "react-i18next";
import i18n from "../lib/i18n";
import { AuthProvider } from "../lib/auth-context";
import { cleanupLegacyCurrencyStorage } from "../lib/currency";
import { WebActivityTracker } from "../components/WebActivityTracker";
import RouteAnnouncer from "../components/ui/RouteAnnouncer";
import { isStaticLegalPath } from "../lib/publicLegalPaths";

// Initialize storage adapter before anything else
import "../lib/storage-init";

export function Providers({ children }: { children: any }) {
  const pathname = usePathname();
  const [mounted, setMounted] = useState(false);
  const [dir, setDir] = useState<"ltr" | "rtl">("ltr");

  useEffect(() => {
    setMounted(true);
    cleanupLegacyCurrencyStorage();
  }, []);

  // Sync document direction with i18n language
  useEffect(() => {
    const updateDir = () => {
      const newDir = i18n.language === "he" ? "rtl" : "ltr";
      setDir(newDir);
      document.documentElement.dir = newDir;
      document.documentElement.lang = i18n.language;
    };

    updateDir();
    i18n.on("languageChanged", updateDir);
    return () => {
      i18n.off("languageChanged", updateDir);
    };
  }, []);

  // The public legal pages render static generated content and call no client
  // hooks, so they skip the providers entirely. Without this they would only
  // appear after hydration, and reviewers that fetch them without JavaScript
  // (Google OAuth verification, Grow site approval) would see an empty page.
  if (isStaticLegalPath(pathname)) {
    return <>{children}</>;
  }

  if (!mounted) {
    return null;
  }

  return (
    <I18nextProvider i18n={i18n}>
      <AuthProvider>
        <WebActivityTracker />
        <RouteAnnouncer />
        {children}
      </AuthProvider>
    </I18nextProvider>
  );
}
